import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import "../Styles/components/customerSearchField.scss";

const toCustomerList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value.toArray === "function") return value.toArray();
    return [];
};

export function CustomerSearchField({
    id,
    value,
    onChange,
    onSearch,
    onClear,
    placeholder,
    disabled = false,
    className = "",
    inputClassName = "",
    style,
    inputStyle,
}) {
    const text = value ?? "";
    const suppressChangeRef = useRef(false);

    const submit = () => {
        if (disabled) return;
        onSearch?.(text);
    };

    // Keep focus on the input so a button press does not blur it and emit an empty change.
    const pressButton = (event) => {
        event.preventDefault();
        suppressChangeRef.current = true;
    };

    return (
        <div className={`customer-search-field subscribers-search-input ${className}`.trim()} style={style}>
            <InputText
                type="text"
                id={id}
                className={`w-full ${inputClassName}`.trim()}
                style={inputStyle}
                placeholder={placeholder}
                value={text}
                disabled={disabled}
                autoComplete="off"
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        submit();
                    }
                }}
                onChange={(e) => {
                    if (suppressChangeRef.current) {
                        suppressChangeRef.current = false;
                        return;
                    }
                    onChange?.(e.target.value);
                }}
            />
            <Button
                type="button"
                onMouseDown={pressButton}
                onClick={() => {
                    suppressChangeRef.current = false;
                    if (text) submit();
                }}
                icon="pi pi-search"
                className="p-button-info p-button-text subscribers-search-btn"
                disabled={disabled}
            />
            {text ? (
                <Button
                    type="button"
                    onMouseDown={pressButton}
                    onClick={() => {
                        suppressChangeRef.current = false;
                        onClear?.();
                    }}
                    icon="pi pi-times"
                    className="p-button-danger p-button-text subscribers-search-clear-btn"
                    disabled={disabled}
                />
            ) : null}
        </div>
    );
}

function CustomerSuggestionBody({ customer, compact }) {
    const tags = Array.isArray(customer.tags) ? customer.tags : [];

    return (
        <div className={`global-search-item${compact ? " global-search-item-compact" : ""}`}>
            <div className={`grid global-search-item-details${compact ? " global-search-item-details-compact" : ""}`}>
                <div className={`${compact ? "col-12" : "col-7"} global-search-item-column global-search-item-info`}>
                    {customer.name ? <span className="customerNameAutoComplete">{customer.name}</span> : null}
                    {customer.phoneNo ? <span className="customerPhoneNoAutoComplete">{customer.phoneNo}</span> : null}
                    {customer.email ? <span className="customerEmailAutoComplete">{customer.email}</span> : null}
                    {customer.customerNum ? <span className="customerRefAutoComplete">{customer.customerNum}</span> : null}
                    {customer.reference ? <span className="customerIdAutoComplete">{customer.reference}</span> : null}
                </div>
                {(customer.segmentName || tags.length > 0) ? (
                    <div className={`${compact ? "col-12" : "col-5"} global-search-item-column global-search-item-labels${compact ? " global-search-item-labels-compact" : ""}`}>
                        {customer.segmentName ? (
                            <div className="segment-tag-div global-search-segment">
                                <span className="segment-class">{customer.segmentName}</span>
                            </div>
                        ) : null}
                        {tags.length > 0 ? (
                            <section className="global-search-item-tags">
                                {tags.map((tag) => (
                                    <div key={tag.id || tag.name} className="text-left tag-div-main tag-class" title={tag.name}>
                                        {tag.name}
                                    </div>
                                ))}
                            </section>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function CustomerSearchResults({ open, anchorRef, suggestions, onSelect, onClose, compact, emptyLabel, loading, inline = false }) {
    const panelRef = useRef(null);
    const [box, setBox] = useState(null);
    const list = toCustomerList(suggestions);

    useEffect(() => {
        if (!open || inline) {
            setBox(null);
            return undefined;
        }

        const update = () => {
            const el = anchorRef?.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const width = Math.max(rect.width, compact ? 280 : 448);
            const maxLeft = Math.max(8, window.innerWidth - width - 8);
            setBox({
                top: rect.bottom + 4,
                left: Math.min(Math.max(8, rect.left), maxLeft),
                width: Math.min(width, window.innerWidth - 16),
            });
        };

        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [open, anchorRef, compact, list.length, loading, inline]);

    useEffect(() => {
        if (!open) return undefined;

        const onPointer = (event) => {
            if (panelRef.current?.contains(event.target)) return;
            if (anchorRef?.current?.contains(event.target)) return;
            onClose?.();
        };
        const onKey = (event) => {
            if (event.key === "Escape") onClose?.();
        };

        document.addEventListener("mousedown", onPointer);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onPointer);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, onClose, anchorRef]);

    if (!open || (!inline && !box)) return null;

    const panel = (
        <div
            ref={panelRef}
            className={`customer-search-results${compact ? " customer-search-results-compact" : ""}${inline ? " customer-search-results-inline" : ""}`}
            style={inline ? undefined : { top: box.top, left: box.left, width: box.width }}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
        >
            {loading ? (
                <div className="customer-search-results-status">
                    <ProgressSpinner style={{ width: "28px", height: "28px" }} strokeWidth="4" />
                </div>
            ) : list.length === 0 ? (
                <div className="customer-search-results-status">{emptyLabel}</div>
            ) : (
                list.map((customer, index) => (
                    <button
                        type="button"
                        key={customer.id ?? customer.bcId ?? index}
                        className="customer-search-result"
                        onClick={() => onSelect?.(customer)}
                    >
                        <CustomerSuggestionBody customer={customer} compact={compact} />
                        {index !== list.length - 1 ? <hr className="hr_customers" /> : null}
                    </button>
                ))
            )}
        </div>
    );

    if (inline) return panel;
    return createPortal(panel, document.body);
}

export function CustomerSearchPicker({
    id,
    value,
    onChange,
    onSearch,
    onClear,
    placeholder,
    disabled,
    suggestions,
    open,
    onClose,
    onSelect,
    compact = false,
    emptyLabel,
    loading = false,
    inline = false,
    className = "",
    style,
}) {
    const anchorRef = useRef(null);

    return (
        <div
            ref={anchorRef}
            className={`customer-search-picker${compact ? " customer-search-picker-compact" : ""} ${className}`.trim()}
            style={style}
        >
            <CustomerSearchField
                id={id}
                value={value}
                onChange={onChange}
                onSearch={onSearch}
                onClear={onClear}
                placeholder={placeholder}
                disabled={disabled}
            />
            <CustomerSearchResults
                open={open}
                anchorRef={anchorRef}
                suggestions={suggestions}
                onSelect={onSelect}
                onClose={onClose}
                compact={compact}
                emptyLabel={emptyLabel}
                loading={loading}
                inline={inline}
            />
        </div>
    );
}
